import { useState, type ReactNode } from 'react'
import { AutocompleteCustomerContainer } from '@/components/AutocompleteCustomerContainer'
import { FormCapsLabel } from '@/components/ui/form-caps-label'
import { FormFieldLabel } from '@/components/ui/form-field-label'
import { UnderlineInput } from '@/components/ui/underline-input'
import { UnderlineTextarea } from '@/components/ui/underline-textarea'
import { type SearchOption } from '@/components/ui/Search'

export interface NewJobInitialValues {
  customerId?: string
  customerName: string
  deviceType?: string
  deviceDescription?: string
  devicePassword?: string
  fault: string
  peripherals?: string
}

export interface NewJobScreenProps {
  initialValues?: NewJobInitialValues
  errorMessage?: string | null
  showHeader?: boolean
  onSave?: (values: NewJobInitialValues) => void | Promise<void>
  onCancel?: () => void
  extra?: ReactNode
}

const defaultInitialValues: NewJobInitialValues = {
  customerName: '',
  fault: '',
}

export function NewJobScreen({
  initialValues,
  errorMessage,
  onSave,
  onCancel,
  extra,
}: NewJobScreenProps) {
  const initial = initialValues ?? defaultInitialValues
  const [selectedCustomer, setSelectedCustomer] = useState<SearchOption | null>(null)
  const [customerName, setCustomerName] = useState(initial.customerName)
  const [deviceType, setDeviceType] = useState(initial.deviceType ?? '')
  const [deviceDescription, setDeviceDescription] = useState(initial.deviceDescription ?? '')
  const [devicePassword, setDevicePassword] = useState(initial.devicePassword ?? '')
  const [fault, setFault] = useState(initial.fault)
  const [peripherals, setPeripherals] = useState(initial.peripherals ?? '')

  const setFromCustomer = (customer: SearchOption | null) => {
    setSelectedCustomer(customer)
    setCustomerName(customer && typeof customer.name === 'string' ? customer.name : '')
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    await onSave?.({
      customerId: typeof selectedCustomer?.id === 'string' ? selectedCustomer.id : '',
      customerName,
      deviceType,
      deviceDescription,
      devicePassword,
      fault,
      peripherals,
    })
  }

  return (
    <form className="space-y-6 p-6 text-sm" onSubmit={handleSubmit}>
      <div className="space-y-2">
        <FormCapsLabel>Customer</FormCapsLabel>
        <AutocompleteCustomerContainer
          value={selectedCustomer}
          onChange={setFromCustomer}
          ariaLabel="Customer for new job"
          allowCreateOption
        />
        <UnderlineInput
          value={customerName}
          onChange={(event) => setCustomerName(event.target.value)}
          placeholder="Customer name"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <FormFieldLabel>Type</FormFieldLabel>
          <UnderlineInput value={deviceType} onChange={(event) => setDeviceType(event.target.value)} />
        </div>
        <div className="space-y-2">
          <FormFieldLabel>Device(s) description</FormFieldLabel>
          <UnderlineInput
            value={deviceDescription}
            onChange={(event) => setDeviceDescription(event.target.value)}
            placeholder="Brand, model, quantity, or identifying notes"
          />
        </div>
      </div>

      <div className="space-y-2">
        <FormFieldLabel>Device password</FormFieldLabel>
        <UnderlineInput value={devicePassword} onChange={(event) => setDevicePassword(event.target.value)} />
      </div>

      <div className="space-y-2">
        <FormFieldLabel>Fault</FormFieldLabel>
        <UnderlineTextarea value={fault} onChange={(event) => setFault(event.target.value)} />
      </div>

      <div className="space-y-2">
        <FormFieldLabel>Peripherals</FormFieldLabel>
        <UnderlineTextarea value={peripherals} onChange={(event) => setPeripherals(event.target.value)} />
      </div>

      {errorMessage ? <p className="text-sm text-red-600">{errorMessage}</p> : null}
      {extra}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-md border px-4 py-2 text-sm">
          Cancel
        </button>
        <button type="submit" className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white">
          Create Job
        </button>
      </div>
    </form>
  )
}
